import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import ProductImage from "./ProductImage";
import productData from "../Datas/productData";
import {
  formatPrice,
  getCartItemKey,
  getProductPrice,
} from "../Utils/product";
import "../css/cartpage.scss";

const STANDARD_SHIPPING = 9.99;

const scrollToTop = () =>
  window.scrollTo({ top: 0, behavior: "smooth" });

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } =
    useContext(CartContext);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponMessage, setCouponMessage] = useState("");

  const orderItems = useMemo(
    () =>
      cartItems
        .map((item) => {
          const product = productData.find((entry) => entry.id === item.id);
          if (!product) return null;

          const unitPrice = getProductPrice(product, item.variant);
          return {
            ...item,
            product,
            unitPrice,
            lineTotal: unitPrice * item.quantity,
          };
        })
        .filter(Boolean),
    [cartItems]
  );

  const subtotal = orderItems.reduce(
    (total, item) => total + item.lineTotal,
    0
  );
  const discount = appliedCoupon ? subtotal * appliedCoupon.rate : 0;
  const shipping = STANDARD_SHIPPING;
  const total = Math.max(0, subtotal - discount + shipping);

  const handleCouponSubmit = (event) => {
    event.preventDefault();
    const normalizedCode = couponCode.trim().toUpperCase();

    if (normalizedCode === "SICKZUNO10") {
      setAppliedCoupon({ code: normalizedCode, rate: 0.1 });
      setCouponMessage("SICKZUNO10 applied — you saved 10%.");
      return;
    }

    setAppliedCoupon(null);
    setCouponMessage(
      normalizedCode
        ? "That code is not available. Try SICKZUNO10."
        : "Enter a discount code first."
    );
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponMessage("Discount code removed.");
  };

  const handleCheckout = () => {
    navigate("/checkout", {
      state: { discountCode: appliedCoupon?.code || null },
    });
    scrollToTop();
  };

  if (orderItems.length === 0) {
    return (
      <main className="cart-page cart-page--empty" id="main-content">
        <section className="cart-empty" aria-labelledby="empty-cart-title">
          <span className="cart-empty__eyebrow">Your pantry awaits</span>
          <h1 id="empty-cart-title">Your cart is empty</h1>
          <p>
            Explore small-batch spices, fragrant herbs and globally inspired
            seasonings for your next meal.
          </p>
          <Link className="cart-button cart-button--primary" to="/shop" onClick={scrollToTop}>
            Shop all ingredients
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="cart-page" id="main-content">
      <div className="cart-page__shell">
        <header className="cart-page__heading">
          <span className="cart-page__eyebrow">sickzuno</span>
          <h1>Your cart</h1>
          <p>
            {orderItems.reduce((count, item) => count + item.quantity, 0)} items
            ready for your pantry.
          </p>
        </header>

        <div className="cart-page__layout">
          <section className="cart-panel" aria-labelledby="cart-items-title">
            <h2 className="cart-panel__title" id="cart-items-title">
              Cart items
            </h2>

            <div className="cart-items">
              {orderItems.map((item) => {
                const itemKey = getCartItemKey(item);

                return (
                  <article className="cart-item" key={itemKey}>
                    <Link
                      className="cart-item__thumb"
                      to={`/shopDetails/${item.product.id}`}
                      onClick={scrollToTop}
                      aria-label={`View ${item.product.name}`}
                    >
                      <ProductImage
                        product={item.product}
                        className="cart-item__image"
                      />
                    </Link>

                    <div className="cart-item__details">
                      <p className="cart-item__sku">
                        {item.product.sku || "Pantry ingredient"}
                      </p>
                      <h3>
                        <Link
                          to={`/shopDetails/${item.product.id}`}
                          onClick={scrollToTop}
                        >
                          {item.product.name}
                        </Link>
                      </h3>
                      <dl className="cart-item__options">
                        {item.variant && (
                          <div>
                            <dt>Variety</dt>
                            <dd>{item.variant}</dd>
                          </div>
                        )}
                        {item.packaging && (
                          <div>
                            <dt>Pack size</dt>
                            <dd>{item.packaging}</dd>
                          </div>
                        )}
                      </dl>
                      <button
                        className="cart-item__remove"
                        type="button"
                        onClick={() =>
                          removeFromCart(
                            item.id,
                            item.variant,
                            item.packaging
                          )
                        }
                        aria-label={`Remove ${item.product.name} from cart`}
                      >
                        Remove
                      </button>
                    </div>

                    <div className="cart-item__purchase">
                      <span className="cart-item__unit-price">
                        {formatPrice(item.unitPrice)} each
                      </span>
                      <div
                        className="cart-quantity"
                        aria-label={`Quantity for ${item.product.name}`}
                      >
                        <button
                          type="button"
                          disabled={item.quantity <= 1}
                          onClick={() =>
                            updateQuantity(
                              item.id,
                              item.variant,
                              item.packaging,
                              item.quantity - 1
                            )
                          }
                          aria-label={`Decrease ${item.product.name} quantity`}
                        >
                          <span aria-hidden="true">−</span>
                        </button>
                        <span className="cart-quantity__value" aria-live="polite">
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          disabled={item.quantity >= 99}
                          onClick={() =>
                            updateQuantity(
                              item.id,
                              item.variant,
                              item.packaging,
                              item.quantity + 1
                            )
                          }
                          aria-label={`Increase ${item.product.name} quantity`}
                        >
                          <span aria-hidden="true">+</span>
                        </button>
                      </div>
                      <strong className="cart-item__line-total">
                        {formatPrice(item.lineTotal)}
                      </strong>
                    </div>
                  </article>
                );
              })}
            </div>

            <Link className="cart-text-link" to="/shop" onClick={scrollToTop}>
              ← Continue shopping
            </Link>
          </section>

          <aside className="cart-summary" aria-labelledby="order-summary-title">
            <h2 id="order-summary-title">Order summary</h2>

            <div className="shipping-message">
              <p>
                Flat-rate tracked shipping is {formatPrice(STANDARD_SHIPPING)}.
              </p>
              <progress
                max="100"
                value="100"
                aria-label="Flat-rate shipping selected"
              />
            </div>

            <form className="discount-form" onSubmit={handleCouponSubmit}>
              <label htmlFor="discount-code">Discount code</label>
              <div className="discount-form__controls">
                <input
                  id="discount-code"
                  name="discountCode"
                  type="text"
                  value={couponCode}
                  onChange={(event) => setCouponCode(event.target.value)}
                  placeholder="Try SICKZUNO10"
                  autoComplete="off"
                  disabled={Boolean(appliedCoupon)}
                />
                {appliedCoupon ? (
                  <button type="button" onClick={removeCoupon}>
                    Remove
                  </button>
                ) : (
                  <button type="submit">Apply</button>
                )}
              </div>
              <p className="discount-form__message" role="status">
                {couponMessage}
              </p>
            </form>

            <dl className="cart-summary__totals">
              <div>
                <dt>Subtotal</dt>
                <dd>{formatPrice(subtotal)}</dd>
              </div>
              {discount > 0 && (
                <div className="cart-summary__discount">
                  <dt>Discount</dt>
                  <dd>−{formatPrice(discount)}</dd>
                </div>
              )}
              <div>
                <dt>Shipping</dt>
                <dd>{formatPrice(shipping)}</dd>
              </div>
              <div className="cart-summary__total">
                <dt>
                  Total <small>USD</small>
                </dt>
                <dd>{formatPrice(total)}</dd>
              </div>
            </dl>

            <button
              className="cart-button cart-button--primary cart-summary__checkout"
              type="button"
              onClick={handleCheckout}
            >
              Continue to checkout
            </button>
            <p className="cart-summary__notice">
              Payment details are completed on the secure payment page.
            </p>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default CartPage;
